import type { CSSProperties } from 'react';
import type { ProposalTheme } from '@shared/types';
import { deriveBrandPalette, isUsableBrandColor } from '@shared/brand-palette';

/**
 * Momentum 3 (design.md V15 "Momentum v2"/V15 third revision) is now the
 * ONLY deck design the web app renders (`standard_v1`, `momentum_v1` and
 * `momentum_v2` are sunset - see `web/src/pages/registry.tsx`). Mint
 * `#34E5A4` (structure - tags, numbers, rules, the primary donut slice),
 * mint-tint `#D8F8EE` (stage bars on light, total rows, rationale bars) and
 * lime `#D6F55A` (dark-surface emphasis only - stage bars on dark heroes,
 * the close CTA chip; never on light, never alongside mint on one element).
 * Inter-first stack, no webfont CDN. These three ride
 * `--doc-primary/secondary/accent`, so a rep's own brand colors still apply
 * "on top of" the family with zero extra plumbing
 * (`web/src/styles/momentum-v3.css` maps `--mv3-mint/--mv3-mint-tint/
 * --mv3-lime` straight to them).
 */
export const MOMENTUM_V3_TEMPLATE_ID = 'momentum_v3';

export const MOMENTUM_V3_DEFAULTS = {
  primaryColor: '#34E5A4',
  secondaryColor: '#D8F8EE',
  accentColor: '#D6F55A',
  headingFont: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif",
  bodyFont: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif",
};

export function isMomentumV3Template(theme: ProposalTheme | null | undefined): boolean {
  return theme?.templateId === MOMENTUM_V3_TEMPLATE_ID;
}

/**
 * Maps a document theme onto CSS custom properties. Applied to the same
 * `.paper` root in both the workspace and print/share views, so every block
 * renders identically no matter which shell it sits inside. Momentum 3 is
 * the only deck design the web app renders (see `pages/registry.tsx`), so
 * every theme - including a legacy record still tagged `standard_v1` /
 * `momentum_v1` / `momentum_v2` - gets the momentum_v3 default palette; a
 * rep's own brand colors (when set) still win either way.
 */
export function themeStyle(theme: ProposalTheme | null | undefined): CSSProperties {
  const defaults = MOMENTUM_V3_DEFAULTS;
  const vars: Record<string, string> = {
    '--doc-primary': theme?.primaryColor || defaults.primaryColor,
    '--doc-secondary': theme?.secondaryColor || defaults.secondaryColor,
    '--doc-accent': theme?.accentColor || defaults.accentColor,
    '--doc-heading-font': theme?.headingFont || defaults.headingFont,
    '--doc-body-font': theme?.bodyFont || defaults.bodyFont,
  };

  // V16 "client brand color" (Stream A), momentum_v3 only: one stored brand
  // color drives a full derived OKLCH palette (src/domain/brand-palette.ts),
  // including the dark surfaces - but only when it's actually active
  // (brandColorSource logo/rep, never 'off') and usable (isUsableBrandColor
  // rejects near-neutrals - a black/white/gray brand color would derive an
  // unreadable palette). An explicit primaryColor/secondaryColor/accentColor
  // still wins over the derived value for THAT slot only, so a rep who
  // dialed in one exact override keeps it even with a brand color active.
  // Absent any of this, emit exactly what's emitted today - no new vars, so
  // every other family and the default v3 look stay byte-for-byte unchanged.
  if (
    isMomentumV3Template(theme) &&
    theme?.brandColor &&
    (theme.brandColorSource === 'logo' || theme.brandColorSource === 'rep') &&
    isUsableBrandColor(theme.brandColor)
  ) {
    const palette = deriveBrandPalette(theme.brandColor);
    if (!theme.primaryColor) vars['--doc-primary'] = palette.primary;
    if (!theme.secondaryColor) vars['--doc-secondary'] = palette.secondary;
    if (!theme.accentColor) vars['--doc-accent'] = palette.accent;
    vars['--doc-primary-deep'] = palette.primaryDeep;
    vars['--doc-dark-bg'] = palette.darkBg;
    vars['--doc-dark-bg-2'] = palette.darkBg2;
    vars['--doc-dark-text-2'] = palette.darkText2;
    vars['--doc-dark-text-3'] = palette.darkText3;
  }

  return vars as CSSProperties;
}

/** brandName text when there is no logo; falls back to the advertiser name. */
export function brandNameFor(theme: ProposalTheme | null | undefined, fallback: string): string {
  return theme?.brandName?.trim() || fallback;
}
